import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MenuWithDropdown from "./MenuWithDropdown";
import useNavigation from "../hooks/useNavigation";
import { NavigationItem } from "../types";
import logo from "../imgs/wwf_logo.png";

export default function MainHeader() {
  const theme = useTheme();
  const { navigation, loading, error } = useNavigation();

  const StyledLink = ({ title, url }: { title: string; url: string }) => {
    return (
      <Box
        flexDirection="row"
        alignSelf="center"
      >
        <Button
          component={Link}
          style={{
            textDecoration: "none",
            color: theme.palette.primary.contrastText,
          }}
          to={url}
        >
          <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>{title}</Typography>
        </Button>
      </Box>
    );
  };

  const matches = useMediaQuery("(max-width:768px)");

  // mobile menu open controls
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // submenus open controls
  const [openSubMenus, setOpenSubMenus] = React.useState<{[key: string]: boolean}>({});
  const toggleSubMenu = (title: string) => {
    setOpenSubMenus(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  const renderNavItem = (item: NavigationItem, index: number) => {
    if (item.type === 'link') {
      return <StyledLink key={index} title={item.title} url={item.url} />;
    } else {
      const links = item.links.map(link => ({ name: link.title, path: link.url }));
      return (
        <MenuWithDropdown
          key={index}
          name={item.title}
          links={links}
        />
      );
    }
  };

  const renderMobileNavItem = (item: NavigationItem, index: number) => {
    if (item.type === 'link') {
      return (
        <MenuItem
          key={index}
          onClick={handleClose}
          component="a"
          href={item.url}
        >
          {item.title}
        </MenuItem>
      );
    } else {
      const isOpen = openSubMenus[item.title] || false;
      return (
        <MenuItem key={index}>
          <List disablePadding>
            <ListItemButton
              onClick={() => toggleSubMenu(item.title)}
              sx={{ padding: 0 }}
            >
              <ListItemText primary={item.title} />
              {isOpen ? <ExpandMore /> : <ExpandLess />}
            </ListItemButton>
            <Collapse
              in={!isOpen}
              timeout="auto"
              unmountOnExit
            >
              <List
                component="div"
                disablePadding
              >
                {item.links.map((link, linkIndex) => (
                  <ListItemButton
                    key={linkIndex}
                    component="a"
                    href={link.url}
                    sx={{ pl: 2 }}
                  >
                    <ListItemText
                      primary={link.title}
                      sx={{ whiteSpace: "normal" }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </List>
        </MenuItem>
      );
    }
  };

  if (loading) {
    return (
      <Box>
        <img
          className="logo"
          src={logo}
          alt="wwf"
        />
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          sx={{
            width: "100%",
            background: theme.palette.primary.main,
          }}
        >
          <Button
            style={{
              textDecoration: "none",
              color: theme.palette.primary.contrastText,
              cursor: "default",
            }}
            disabled
          >
            <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>Loading...</Typography>
          </Button>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <img
          className="logo"
          src={logo}
          alt="wwf"
        />
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          sx={{
            width: "100%",
            background: theme.palette.primary.main,
          }}
        >
          <Button
            style={{
              textDecoration: "none",
              color: theme.palette.primary.contrastText,
              cursor: "default",
            }}
            disabled
          >
            <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>Error loading navigation</Typography>
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <img
        className="logo"
        src={logo}
        alt="wwf"
      />
      {!matches && (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          sx={{
            width: "100%",
            background: theme.palette.primary.main,
          }}
        >
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-around"
            alignItems="center"
            width="60%"
          >
            {navigation.map((item, index) => renderNavItem(item, index))}
          </Box>
        </Box>
      )}
      {matches && (
        <Box
          display="flex"
          flexDirection="row-reverse"
          sx={{
            width: "100%",
            background: theme.palette.primary.main,
          }}
        >
          <Button
            id="basic-button"
            aria-controls={open ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
          >
            <MenuIcon sx={{ color: "#fff", width: "1.5em", height: "1.5em" }} />
          </Button>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            {navigation.map((item, index) => renderMobileNavItem(item, index))}
          </Menu>
        </Box>
      )}
    </Box>
  );
}
